import React from 'react';
import type { ReactNode } from 'react';
import clsx from 'clsx';
import { Colors, type ColorsType } from '@common/tokens';
import { Text } from '@common/components';

export type StatusPillVariantType =
  | 'inProgress'
  | 'warning'
  | 'danger'
  | 'success'
  | 'neutral'
  | 'timeOff';

type StatusPillPalette = { background: ColorsType; color: ColorsType };

const STATUS_PILL_PALETTE: Record<StatusPillVariantType, StatusPillPalette> = {
  inProgress: { background: 'skyLightest', color: 'textDefault' },
  warning: { background: 'sunLighter', color: 'textDefault' },
  danger: { background: 'coralHyperlight', color: 'textDefault' },
  success: { background: 'grassLightest', color: 'textDefault' },
  neutral: { background: 'steelLighter', color: 'textDefault' },
  timeOff: { background: 'violetLightest', color: 'violet' },
};

type StatusPillProps = {
  children: ReactNode;
  variant?: StatusPillVariantType;
  emphasized?: boolean;
  className?: string;
};

export function StatusPill({
  children,
  variant = 'inProgress',
  emphasized = false,
  className,
}: StatusPillProps) {
  const palette = STATUS_PILL_PALETTE[variant];

  return (
    <span className={clsx('wk-status-pill', className)}>
      <Text
        size="sm"
        color={palette.color}
        weight={emphasized ? 'bold' : 'light'}
        transform={emphasized ? 'uppercase' : undefined}
      >
        {children}
      </Text>
      <style jsx>{`
        .wk-status-pill {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          padding: 4px 8px;
          border-radius: 8px;
          background-color: ${Colors[palette.background]};
        }
      `}</style>
    </span>
  );
}
